import { enforceRateLimit } from "./rate-limit.server";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { MAX_UNANSWERED_MESSAGES } from "@/lib/message-limits";
import { countUnanswered, UNANSWERED_LIMIT_MESSAGE } from "@/lib/message-thread";

const SendMessageInput = z.object({
  slug: z.string().min(1),
  body: z.string().trim().min(2).max(2000),
});

const ThreadInput = z.object({
  adId: z.string().uuid(),
  otherUserId: z.string().uuid(),
});

type MessageRow = {
  id: string;
  ad_id: string;
  sender_id: string;
  recipient_id: string;
  body: string;
  read_at: string | null;
  created_at: string;
  deleted_at?: string | null;
  deleted_by?: string | null;
  ads: { slug: string; title: string; image_url: string } | null;
};

/** Texten som visas i stället för ett borttaget meddelande. */
export const DELETED_MESSAGE_TEXT = "Meddelandet togs bort";

export const sendMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SendMessageInput.parse(input))
  .handler(async ({ data, context }) => {
    await enforceRateLimit("message_send", context.userId);
    const { data: ad, error: adError } = await context.supabase
      .from("ads")
      .select("id, user_id, slug, title")
      .eq("slug", data.slug)
      .maybeSingle();

    if (adError || !ad) throw new Error("Annonsen hittades inte.");
    if (!ad.user_id) throw new Error("Den här annonsen går inte att kontakta.");
    if (ad.user_id === context.userId)
      throw new Error("Du kan inte skicka meddelande till din egen annons.");

    const { data: sellerProfile } = await context.supabase
      .from("profiles")
      .select("deletion_pending_at")
      .eq("user_id", ad.user_id)
      .maybeSingle();
    if (sellerProfile?.deletion_pending_at)
      throw new Error("Säljaren har lämnat Sellavo och går inte att kontakta.");


    const quota = await countUnanswered(
      context.supabase as never,
      ad.id,
      context.userId,
      ad.user_id,
    );
    if (quota.remaining <= 0) throw new Error(UNANSWERED_LIMIT_MESSAGE);

    const { error } = await context.supabase.from("messages").insert({
      ad_id: ad.id,
      sender_id: context.userId,
      recipient_id: ad.user_id,
      body: data.body,
    });

    if (error) throw new Error(`Kunde inte skicka meddelandet: ${error.message}`);

    // Notis till säljaren (blockerar aldrig utskicket av meddelandet).
    try {
      const { notifyNewMessage } = await import("@/lib/notifications.server");
      await notifyNewMessage({
        recipientId: ad.user_id,
        senderId: context.userId,
        adId: ad.id,
        adSlug: ad.slug,
        adTitle: ad.title,
        body: data.body,
      });
    } catch (notifyError) {
      console.error("[messages] notis misslyckades:", notifyError);
    }

    return {
      ok: true,
      adId: ad.id,
      recipientId: ad.user_id,
      sent: quota.unanswered + 1,
      remaining: quota.remaining - 1,
      max: quota.max,
    };
  });

export const getMyMessageQuota = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: ad } = await context.supabase
      .from("ads")
      .select("id, user_id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad?.user_id || ad.user_id === context.userId) {
      return {
        sent: 0,
        remaining: MAX_UNANSWERED_MESSAGES,
        max: MAX_UNANSWERED_MESSAGES,
        totalSent: 0,
      };
    }
    const quota = await countUnanswered(
      context.supabase as never,
      ad.id,
      context.userId,
      ad.user_id,
    );
    const { count: total } = await context.supabase
      .from("messages")
      .select("id", { count: "exact", head: true })
      .eq("ad_id", ad.id)
      .eq("sender_id", context.userId)
      .eq("recipient_id", ad.user_id);
    return {
      sent: quota.unanswered,
      remaining: quota.remaining,
      max: quota.max,
      totalSent: total ?? 0,
    };
  });

export const replyInThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    ThreadInput.extend({ body: z.string().trim().min(2).max(2000) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    await enforceRateLimit("message_send", context.userId);
    // Verifiera att användaren redan deltar i konversationen
    const { data: existing } = await context.supabase
      .from("messages")
      .select("id")
      .eq("ad_id", data.adId)
      .or(
        `and(sender_id.eq.${context.userId},recipient_id.eq.${data.otherUserId}),and(sender_id.eq.${data.otherUserId},recipient_id.eq.${context.userId})`,
      )
      .limit(1)
      .maybeSingle();

    if (!existing) throw new Error("Konversationen hittades inte.");

    const { data: otherProfile } = await context.supabase
      .from("profiles")
      .select("deletion_pending_at")
      .eq("user_id", data.otherUserId)
      .maybeSingle();
    if (otherProfile?.deletion_pending_at)
      throw new Error("Personen har lämnat Sellavo och går inte att svara.");



    const quota = await countUnanswered(
      context.supabase as never,
      data.adId,
      context.userId,
      data.otherUserId,
    );
    if (quota.remaining <= 0) throw new Error(UNANSWERED_LIMIT_MESSAGE);

    const { error } = await context.supabase.from("messages").insert({
      ad_id: data.adId,
      sender_id: context.userId,
      recipient_id: data.otherUserId,
      body: data.body,
    });
    if (error) throw new Error(`Kunde inte skicka svaret: ${error.message}`);
    return { ok: true, remaining: quota.remaining - 1 };
  });

export const getInbox = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("messages")
      .select(
        "id, ad_id, sender_id, recipient_id, body, read_at, created_at, deleted_at, deleted_by, ads(slug, title, image_url)",
      )
      .order("created_at", { ascending: false })
      .limit(400);

    if (error) throw new Error(error.message);

    const rows = (data ?? []) as unknown as MessageRow[];
    const threads = new Map<
      string,
      {
        adId: string;
        otherUserId: string;
        slug: string;
        title: string;
        imageUrl: string;
        lastBody: string;
        lastAt: string;
        lastMine: boolean;
        unread: number;
        archived: boolean;
        role: "seller" | "buyer";
      }
    >();

    const { data: archivedRows } = await context.supabase
      .from("message_thread_archives")
      .select("ad_id, other_user_id")
      .eq("user_id", context.userId);
    const archivedKeys = new Set(
      (archivedRows ?? []).map((a: any) => `${a.ad_id}:${a.other_user_id}`),
    );

    const { data: deletedRows } = await context.supabase
      .from("message_thread_deletions")
      .select("ad_id, other_user_id")
      .eq("user_id", context.userId);
    const deletedKeys = new Set(
      (deletedRows ?? []).map((d: any) => `${d.ad_id}:${d.other_user_id}`),
    );

    for (const m of rows) {
      const other = m.sender_id === context.userId ? m.recipient_id : m.sender_id;
      const key = `${m.ad_id}:${other}`;
      if (deletedKeys.has(key)) continue;
      const t = threads.get(key);

      const unread = m.recipient_id === context.userId && !m.read_at ? 1 : 0;
      if (!t) {
        threads.set(key, {
          adId: m.ad_id,
          otherUserId: other,
          slug: m.ads?.slug ?? "",
          title: m.ads?.title ?? "Borttagen annons",
          imageUrl: m.ads?.image_url ?? "",
          lastBody: m.deleted_at ? DELETED_MESSAGE_TEXT : m.body,
          lastAt: m.created_at,
          lastMine: m.sender_id === context.userId,
          unread,
          archived: archivedKeys.has(key),
          role:
            m.recipient_id === context.userId &&
            m.sender_id === other &&
            m.recipient_id !== m.sender_id
              ? "seller"
              : "buyer",
        });
      } else {
        t.unread += unread;
      }
    }

    const list = Array.from(threads.values());
    const { resolveDisplayNames } = await import("@/lib/user-names.server");
    const names = await resolveDisplayNames(list.map((t) => t.otherUserId));
    return list.map((t) => ({
      ...t,
      otherName: names.get(t.otherUserId)?.name ?? "Användare",
      otherIsDemo: names.get(t.otherUserId)?.isDemo ?? false,
      otherPreviousName: names.get(t.otherUserId)?.previousName ?? null,
    }));
  });

export const getThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ThreadInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: messages, error } = await context.supabase
      .from("messages")
      .select(
        "id, ad_id, sender_id, recipient_id, body, read_at, created_at, deleted_at, deleted_by, ads(slug, title, image_url)",
      )
      .eq("ad_id", data.adId)
      .or(
        `and(sender_id.eq.${context.userId},recipient_id.eq.${data.otherUserId}),and(sender_id.eq.${data.otherUserId},recipient_id.eq.${context.userId})`,
      )
      .order("created_at", { ascending: true })
      .limit(200);

    if (error) throw new Error(error.message);

    const rows = (messages ?? []) as unknown as MessageRow[];

    const unreadIds = rows
      .filter((m) => m.recipient_id === context.userId && !m.read_at)
      .map((m) => m.id);
    if (unreadIds.length) {
      await context.supabase
        .from("messages")
        .update({ read_at: new Date().toISOString() })
        .in("id", unreadIds);
    }

    const quota = await countUnanswered(
      context.supabase as never,
      data.adId,
      context.userId,
      data.otherUserId,
    );

    const { data: myReports } = await context.supabase
      .from("message_reports")
      .select("message_id")
      .eq("reporter_id", context.userId)
      .in("message_id", rows.map((m) => m.id).slice(0, 200));
    const reported = new Set((myReports ?? []).map((r: any) => r.message_id as string));

    const { resolveDisplayNames } = await import("@/lib/user-names.server");
    const names = await resolveDisplayNames([data.otherUserId]);

    return {
      ad: rows[0]?.ads ?? null,
      me: context.userId,
      otherName: names.get(data.otherUserId)?.name ?? "Användare",
      otherIsDemo: names.get(data.otherUserId)?.isDemo ?? false,
      otherPreviousName: names.get(data.otherUserId)?.previousName ?? null,
      remaining: quota.remaining,
      max: quota.max,
      messages: rows.map((m) => ({
        id: m.id,
        body: m.deleted_at ? DELETED_MESSAGE_TEXT : m.body,
        created_at: m.created_at,
        mine: m.sender_id === context.userId,
        deleted: !!m.deleted_at,
        deletedByMe: !!m.deleted_at && m.deleted_by === context.userId,
        reported: reported.has(m.id),
      })),
    };
  });

export const getUnreadCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { count, error } = await context.supabase
      .from("messages")
      .select("id", { count: "exact", head: true })
      .eq("recipient_id", context.userId)
      .is("read_at", null)
      .is("deleted_at", null);
    if (error) throw new Error(error.message);

    // Systemnotiser (t.ex. klara skanningar) räknas också, annars stämmer
    // inte siffran i sidhuvudet med det som faktiskt visas i inkorgen.
    const { count: notices } = await context.supabase
      .from("system_notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId)
      .is("read_at", null);

    return {
      count: (count ?? 0) + (notices ?? 0),
      messages: count ?? 0,
      notices: notices ?? 0,
    };
  });

/** Tar bort ett eget meddelande. Texten döljs men platsen finns kvar i tråden. */
export const deleteMessage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ messageId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("messages")
      .update({ deleted_at: new Date().toISOString(), deleted_by: context.userId })
      .eq("id", data.messageId)
      .eq("sender_id", context.userId)
      .is("deleted_at", null)
      .select("id")
      .maybeSingle();

    if (error) throw new Error(`Kunde inte ta bort meddelandet: ${error.message}`);
    if (!row) throw new Error("Meddelandet kunde inte tas bort.");
    return { ok: true };
  });

const ArchiveInput = z.object({
  adId: z.string().uuid(),
  otherUserId: z.string().uuid(),
  archived: z.boolean(),
});

/** Arkiverar eller återställer en konversation för den inloggade användaren. */
export const setThreadArchived = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ArchiveInput.parse(input))
  .handler(async ({ data, context }) => {
    if (data.archived) {
      const { error } = await context.supabase
        .from("message_thread_archives")
        .upsert(
          { user_id: context.userId, ad_id: data.adId, other_user_id: data.otherUserId },
          { onConflict: "user_id,ad_id,other_user_id" },
        );
      if (error) throw new Error(`Kunde inte arkivera konversationen: ${error.message}`);
    } else {
      const { error } = await context.supabase
        .from("message_thread_archives")
        .delete()
        .eq("user_id", context.userId)
        .eq("ad_id", data.adId)
        .eq("other_user_id", data.otherUserId);
      if (error) throw new Error(`Kunde inte återställa konversationen: ${error.message}`);
    }
    return { ok: true, archived: data.archived };
  });

/**
 * Tar bort hela konversationen för den inloggade användaren (soft delete).
 * Meddelandena ligger kvar i databasen och kan hittas i efterhand vid behov.
 */
export const deleteThread = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ThreadInput.parse(input))
  .handler(async ({ data, context }) => {
    const { data: existing } = await context.supabase
      .from("messages")
      .select("id")
      .eq("ad_id", data.adId)
      .or(
        `and(sender_id.eq.${context.userId},recipient_id.eq.${data.otherUserId}),and(sender_id.eq.${data.otherUserId},recipient_id.eq.${context.userId})`,
      )
      .limit(1)
      .maybeSingle();
    if (!existing) throw new Error("Konversationen hittades inte.");

    const { error } = await context.supabase
      .from("message_thread_deletions")
      .upsert(
        { user_id: context.userId, ad_id: data.adId, other_user_id: data.otherUserId },
        { onConflict: "user_id,ad_id,other_user_id" },
      );
    if (error) throw new Error(`Kunde inte ta bort konversationen: ${error.message}`);

    await context.supabase
      .from("message_thread_archives")
      .delete()
      .eq("user_id", context.userId)
      .eq("ad_id", data.adId)
      .eq("other_user_id", data.otherUserId);

    return { ok: true };
  });
